import { Header } from 'common/ui/Header';
import { CampaignCreationCSVUpload } from 'modules/campaign-creation/components/CampaignCreationCSVUpload';
import { useState } from 'react';
import { AtsLists } from 'services/integration/components/AtsLists';
import {
  useIntegration,
  useResolvedIntegration
} from 'services/integration/hooks';
import { LoadingIndicator } from 'common/ui/LoadingIndicator';
import { ATS_LOOKUP_LOADING_TITLE } from 'services/integration/constants';
import { Paths } from 'services/navigation/paths';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { updateCampaign } from 'services/campaign/api';
import { CampaignStatus } from 'services/campaign/types';
import { getTitle } from '../utils/getTitle';

const CampaignCreationImportCandidatesStepHeader = () => {
  const onImportCandidatesBack = useCampaignCreationStore(
    (state) => state.onImportCandidatesBack
  );
  const state = useCampaignCreationStore((state) => state.state);

  return (
    <Header
      currentBreadcrumb={getTitle(state)}
      title="Choose candidate list"
      subtitle="Select candidates from a tear sheet, list, group, or upload their details from a spreadsheet."
      breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
      onBack={() => onImportCandidatesBack()}
    />
  );
};

export const CampaignCreationImportCandidatesStep: React.FC = () => {
  // NOTE: This is if the ATS has the talent pool enabled
  const { isListsAvailable } = useIntegration();
  const { isIntegrationResolved } = useResolvedIntegration();
  const [shouldHideATSLists, setShouldHideATSLists] = useState(false);
  const ATSImportState = useCampaignCreationStore(
    (state) => state.state?.ATSImportState
  );
  const channel = useCampaignCreationStore((state) => state.state?.channel);
  const emailChannelFallbackEnabled = useCampaignCreationStore(
    (state) => state.state?.emailChannelFallbackEnabled
  );
  const csvImportState = useCampaignCreationStore(
    (state) => state.state?.CSVImportState
  );
  const onCSVImportCandidates = useCampaignCreationStore(
    (state) => state.onCSVImportCandidates
  );
  const onCSVImportSkip = useCampaignCreationStore(
    (state) => state.onCSVImportSkip
  );
  const onATSImportCandidates = useCampaignCreationStore(
    (state) => state.onATSImportCandidates
  );

  const [atsListId, setAtsListId] = useState<string | undefined>(
    ATSImportState?.atsListId || undefined
  );

  // Wait for the ATS lookup before choosing a view. `isListsAvailable` is
  // derived from the integration atom, which reads "no ATS" while the answer is
  // still in flight — so branching on it directly opens the CSV dropzone for an
  // ATS org and then swaps it for the talent list once the lookup lands,
  // throwing the recruiter off a form they may already be filling in.
  if (!isIntegrationResolved) {
    return <LoadingIndicator title={ATS_LOOKUP_LOADING_TITLE} />;
  }

  if (isListsAvailable && !shouldHideATSLists) {
    return (
      <>
        <CampaignCreationImportCandidatesStepHeader />
        <AtsLists
          atsListId={atsListId}
          setAtsListId={(atsListId) => {
            setAtsListId(atsListId);
          }}
          onUploadCandidates={() => {
            setShouldHideATSLists(true);
          }}
          setCampaignToDisconnectFromAtsList={async (campaignId) => {
            if (!campaignId) return;
            // NOTE: We do this here, as it can't be saved and will cause issues later on.
            // Also there is a confirmation already shown.
            await updateCampaign({
              id: campaignId,
              status: CampaignStatus.DISCONNECTED
            });
          }}
          // NOTE: This when selecting next, we should rename this.
          onTalkToCandidates={() => {
            if (!atsListId) return;
            onATSImportCandidates({
              atsListId
            });
          }}
        />
      </>
    );
  }

  return (
    <>
      <CampaignCreationImportCandidatesStepHeader />
      <CampaignCreationCSVUpload
        channel={channel || null}
        emailFallbackEnabled={emailChannelFallbackEnabled ?? null}
        csvImportState={csvImportState || null}
        onCSVImportCandidates={onCSVImportCandidates}
        onCSVImportSkip={onCSVImportSkip}
      />
    </>
  );
};
